import { createContext, useState, useCallback, type ReactNode } from 'react';
import type { AuthenticatedUser } from '@m8-app/contracts/rest/auth.js';
import { logout as apiLogout } from '../api/auth.js';

interface AuthState {
  token: string | null;
  user: AuthenticatedUser | null;
}

export interface AuthContextValue extends AuthState {
  login: (token: string, refreshToken: string, user: AuthenticatedUser) => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function loadInitialState(): AuthState {
  const token = localStorage.getItem('accessToken');
  const raw = localStorage.getItem('authUser');
  try {
    const user = raw ? (JSON.parse(raw) as AuthenticatedUser) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadInitialState);

  const login = useCallback((token: string, refreshToken: string, user: AuthenticatedUser) => {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('authUser', JSON.stringify(user));
    setState({ token, user });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try { await apiLogout(refreshToken); } catch { /* ignore */ }
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authUser');
    setState({ token: null, user: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
